import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import SportIcon from "@/components/kit/SportIcon";
import { hueOf, sportColors } from "@/components/kit/sportTheme";

/**
 * A sport you can pick: its icon, its name and one line of real detail (`sub`, e.g. "8 shots"). Button mode (onSelect, aria-pressed) or
 * link mode (href, a plain anchor: pass `as` for a router Link).
 * Each sport has its own hue (sportTheme). Selected is a hue ring, a tinted wash, a glow and a check. Give `layoutId` and the
 * highlight is one element that slides from tile to tile (framer-motion shared layout); leave it out for a plain highlight.
 */
export default function SportTile({ sport, label, sub, selected = false, beta = false, onSelect, href, as, layoutId, hue, className, style, ...rest }) {
  const Tag = href ? (as || "a") : "button";
  const props = href ? { [as ? "to" : "href"]: href } : { type: "button", onClick: onSelect, "aria-pressed": selected };
  const c = sportColors(hue ?? hueOf(sport));
  const wash = { background: c.tint, boxShadow: `inset 0 0 0 2px ${c.ring}, ${c.glow}`, borderRadius: 24 };
  return (
    <Tag
      data-kit-motion
      {...props}
      {...rest}
      style={style}
      className={cn(
        "group relative isolate flex flex-col items-start gap-3 rounded-card border p-3.5 sm:p-4 min-h-[116px] sm:min-h-[132px] text-left transition-[transform,border-color,background-color] duration-state ease-standard active:scale-[.97]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-300/70",
        selected ? "border-transparent -translate-y-0.5 text-white" : "border-white/10 bg-zinc-900/70 text-zinc-200 hover:border-white/25 hover:-translate-y-0.5",
        className
      )}
    >
      {selected && (layoutId
        ? <motion.span layoutId={layoutId} aria-hidden="true" className="absolute inset-0 -z-10" style={wash} transition={{ type: "spring", stiffness: 320, damping: 30 }} />
        : <span aria-hidden="true" className="absolute inset-0 -z-10" style={wash} />)}
      <span className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: c.soft, color: c.text }}>
        <SportIcon sport={sport} size={30} />
      </span>
      <span className="min-w-0 w-full">
        <span className="block text-[15px] lg:text-[17px] font-bold leading-tight">{label}</span>
        {sub ? <span className="block text-[12px] lg:text-[13px] mt-0.5 text-zinc-400">{sub}</span> : null}
      </span>
      {selected ? <span className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full flex items-center justify-center text-black" style={{ background: c.ring }} aria-hidden="true"><Check className="w-3.5 h-3.5" strokeWidth={3.5} /></span> : null}
      {beta ? <span className="absolute top-3 right-3 text-[9px] lg:text-xs uppercase tracking-wide font-bold" style={{ color: c.text, ...(selected ? { right: 38 } : null) }}>Beta</span> : null}
    </Tag>
  );
}
